import { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  message,
  InputNumber,
  Badge,
  Tooltip,
  Breadcrumb
} from 'antd';
import {
  WarningOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
  DashboardOutlined,
  CameraOutlined,
  BellOutlined,
  BarChartOutlined,
  LockOutlined,
  SettingOutlined,
  DragOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from "../../../utils/axios";
import { useCaseRegistry } from "./useCaseRegistry";
import { widgetRegistry } from "./widgetRegistry";
import { pageRegistry } from './pageRegistry';
import { widgetPreviewRegistry } from "./widgetPreviewRegistry";
import { useNavigate } from 'react-router-dom';

export const renderWidgetContent = (widget) => {
  const Preview = widgetPreviewRegistry[widget.id];

  if (Preview) return <Preview />;

  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-400">
      <DashboardOutlined className="text-3xl mb-2" />
      <div className="text-xs">
        {widget.title} preview
      </div>
    </div>
  );
};

const WidgetCard = ({ widget, onRemove, isLocked }) => (
  <div className="bg-white rounded-xl shadow-sm p-5 h-full border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all duration-200 group">
    
    {/* HEADER */}
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-gray-800">{widget.title}</h3>
        {widget.type === 'graph' && (
          <Badge count={<BarChartOutlined className="text-blue-500 text-xs" />} />
        )}
      </div>
      {!isLocked && (
        <Tooltip title="Remove widget">
          <Button
            type="text"
            danger
            size="small"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
            icon={<DeleteOutlined />}
            onClick={() => onRemove(widget.id)}
          />
        </Tooltip>
      )}
    </div>

    {/* BODY */}
    <div className="h-[130px]">
      {renderWidgetContent(widget)}
    </div>
  </div>
);

const GridCell = ({
  widget,
  rowIndex,
  colIndex,
  onDrop,
  onDragOver,
  onRemove,
  isLocked
}) => (
  <div
    onDrop={(e) => onDrop(e, rowIndex, colIndex)}
    onDragOver={onDragOver}
    className={`border-2 border-dashed rounded-xl min-h-[180px] p-4 transition-all duration-200 ${
      widget 
        ? 'bg-white border-transparent' 
        : 'bg-gradient-to-br from-gray-50 to-white hover:from-blue-50 hover:to-blue-50/30 border-gray-200 hover:border-blue-300'
    }`}
  >
    {widget ? (
      <WidgetCard widget={widget} onRemove={onRemove} isLocked={isLocked} />
    ) : (
      <div className="text-gray-400 text-sm flex flex-col items-center justify-center h-full">
        {!isLocked && (
          <>
            <PlusOutlined className="text-2xl mb-2 opacity-50" />
            <span>Drop widget here</span>
          </>
        )}
      </div>
    )}
  </div>
);

const CameraDefaultView = () => (
  <Card 
    title={
      <div className="flex items-center gap-2">
        <CameraOutlined className="text-blue-600" />
        <span>Camera Overview</span>
      </div>
    }
    className="shadow-sm border-gray-100"
  >
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
            <th className="p-3 text-left font-semibold text-gray-700">Camera ID</th>
            <th className="p-3 text-left font-semibold text-gray-700">Location</th>
            <th className="p-3 text-left font-semibold text-gray-700">Status</th>
            <th className="p-3 text-left font-semibold text-gray-700">Last Active</th>
            <th className="p-3 text-left font-semibold text-gray-700">Health</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          <tr className="hover:bg-gray-50 transition-colors">
            <td className="p-3 font-medium">CAM-01</td>
            <td className="p-3">Entrance</td>
            <td className="p-3">
              <Badge status="success" text="Online" />
            </td>
            <td className="p-3 text-gray-600">2 mins ago</td>
            <td className="p-3">
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">Good</span>
            </td>
          </tr>
          <tr className="hover:bg-gray-50 transition-colors">
            <td className="p-3 font-medium">CAM-02</td>
            <td className="p-3">Dining Area</td>
            <td className="p-3">
              <Badge status="success" text="Online" />
            </td>
            <td className="p-3 text-gray-600">5 mins ago</td>
            <td className="p-3">
              <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium">Average</span>
            </td>
          </tr>
          <tr className="hover:bg-gray-50 transition-colors">
            <td className="p-3 font-medium">CAM-03</td>
            <td className="p-3">Kitchen</td>
            <td className="p-3">
              <Badge status="error" text="Offline" />
            </td>
            <td className="p-3 text-gray-600">1 hr ago</td>
            <td className="p-3">
              <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">Critical</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Card>
);

const AlertDefaultView = () => (
  <Card 
    title={
      <div className="flex items-center gap-2">
        <BellOutlined className="text-red-600" />
        <span>System Alerts</span>
      </div>
    }
    className="shadow-sm border-gray-100"
  >
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
            <th className="p-3 text-left font-semibold text-gray-700">Alert</th>
            <th className="p-3 text-left font-semibold text-gray-700">Severity</th>
            <th className="p-3 text-left font-semibold text-gray-700">Camera</th>
            <th className="p-3 text-left font-semibold text-gray-700">Time</th>
            <th className="p-3 text-left font-semibold text-gray-700">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          <tr className="hover:bg-gray-50 transition-colors">
            <td className="p-3 font-medium">Crowd Limit Exceeded</td>
            <td className="p-3">
              <Badge status="error" text="High" />
            </td>
            <td className="p-3">CAM-01</td>
            <td className="p-3 text-gray-600">10:42 AM</td>
            <td className="p-3">
              <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium">Open</span>
            </td>
          </tr>
          <tr className="hover:bg-gray-50 transition-colors">
            <td className="p-3 font-medium">Camera Offline</td>
            <td className="p-3">
              <Badge status="warning" text="Medium" />
            </td>
            <td className="p-3">CAM-03</td>
            <td className="p-3 text-gray-600">09:30 AM</td>
            <td className="p-3">
              <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">Unresolved</span>
            </td>
          </tr>
          <tr className="hover:bg-gray-50 transition-colors">
            <td className="p-3 font-medium">Unauthorized Access</td>
            <td className="p-3">
              <Badge status="error" text="High" />
            </td>
            <td className="p-3">CAM-02</td>
            <td className="p-3 text-gray-600">Yesterday</td>
            <td className="p-3">
              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">Resolved</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Card>
);

const getPageMeta = (pageKey) => pageRegistry[pageKey] || {};

const DashboardGateCard = ({ type, onAction }) => {
  const config = {
    BLOCK_PREVIOUS_STEP: {
      title: "Complete Camera Model Configuration",
      description:
        "You must complete and lock camera model configuration before building the dashboard.",
      actionText: "Go to Camera Model Configuration",
      action: () => onAction("MODEL_CONFIG"),
      color: "from-orange-500 to-red-500",
      icon: <WarningOutlined className="text-4xl text-white" />,
      badge: "Action Required"
    },
    ALLOW_REBUILD: {
      title: "Rebuild Dashboard",
      description:
        "You have already configured this dashboard once. You may rebuild it one final time.",
      actionText: "Rebuild Dashboard",
      action: () => onAction("REBUILD"),
      color: "from-blue-500 to-indigo-600",
      icon: <ReloadOutlined className="text-4xl text-white" />,
      badge: "One Rebuild Left"
    },
    BLOCK_REBUILD_LIMIT: {
      title: "Dashboard Locked",
      description:
        "You have exhausted the maximum number of dashboard rebuild attempts.",
      actionText: null,
      action: null,
      color: "from-gray-600 to-gray-800",
      icon: <LockOutlined className="text-4xl text-white" />,
      badge: "Locked"
    }
  };

  const cfg = config[type];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <Card className="max-w-xl w-full rounded-3xl shadow-2xl border-0 overflow-hidden">
        
        {/* HEADER */}
        <div
          className={`p-8 bg-gradient-to-r ${cfg.color} text-white relative`}
        >
          {/* Status Badge */}
          <span className="absolute top-4 right-4 px-3 py-1 text-xs font-semibold rounded-full bg-white/20 backdrop-blur">
            {cfg.badge}
          </span>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/20 backdrop-blur">
              {cfg.icon}
            </div>

            <div>
              <h2 className="text-2xl font-bold leading-tight">
                {cfg.title}
              </h2>
              <p className="text-sm opacity-90 mt-1">
                {cfg.description}
              </p>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="p-8 space-y-6">
          {/* Info Row */}
          <div className="flex items-start gap-3 text-gray-600 text-sm">
            <CheckCircleOutlined className="text-green-500 mt-1" />
            <span>
              Your project data is safe. Existing configurations will remain unchanged.
            </span>
          </div>

          {/* ACTION */}
          {cfg.actionText && (
            <Button
              type="primary"
              size="large"
              block
              className="h-12 text-base font-semibold flex items-center justify-center gap-2"
              onClick={cfg.action}
            >
              {cfg.actionText}
              <ArrowRightOutlined />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

const DashboardBuilder = () => {
  const navigate = useNavigate();
  const [setupStep, setSetupStep] = useState(0);
  const user = JSON.parse(localStorage.getItem('user'));
  const projectName = localStorage.getItem('projectname');
  const organizationId = localStorage.getItem('organizationId');
  const username = user?.username;
  const [projectDetails, setProjectDetails] = useState(null)
  const [allowedPages, setAllowedPages] = useState([]);
  const [allowedWidgetsByPage, setAllowedWidgetsByPage] = useState({});
  const [loading, setLoading] = useState(false)
  const [dashboardGate, setDashboardGate] = useState(null);
  const [projectStatus, setProjectStatus] = useState(null);

  const defaultRows = [
    { cols: 4, widgets: Array(4).fill(null) },
    { cols: 2, widgets: Array(2).fill(null) }
  ];

  const createPageConfig = () => {
  return Object.keys(pageRegistry).reduce((acc, key) => {
    acc[key] = {
      enabled: false,
      rows: JSON.parse(JSON.stringify(defaultRows))
    };
    return acc;
  }, {});
};


  // ✅ ALL HOOKS — ALWAYS CALLED
  const [pageConfig, setPageConfig] = useState(createPageConfig);
  const [selectedPage, setSelectedPage] = useState(allowedPages[0] || 'dashboard');
  const [isLocked, setIsLocked] = useState(false);
  const [draggedWidget, setDraggedWidget] = useState(null);


 const systemPages = ['camera', 'alert'];

const isBuilderPage =
  pageConfig[selectedPage]?.enabled &&
  !systemPages.includes(selectedPage);



  const getProjectDetails = async () => {
    try {
      const res = await axios.post("/get-project-details", { projectName, organizationId });

      if (res?.data?.project) {
        setProjectDetails(res.data.project);
        setProjectStatus(res.data.status); 
      }
    } catch(err) {
      if (err.response && err.response.data) {
        message.warning(
          err.response?.data?.message ||
          err.response?.data?.msg ||
          "Something went wrong"
        );

      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
        message.error('User not authorised');
      } else {
        message.error('Something went wrong');
      }
    }
  };

  useEffect(()=> {
    getProjectDetails()
  },[projectName])

  useEffect(() => {
    if (!projectDetails) return;

    const activeUseCases = (projectDetails.selections || [])
      .flatMap(s => (s.useCases || []).map(uc => useCaseRegistry[uc.id]))
      .filter(Boolean);

    setAllowedPages([
      ...new Set(activeUseCases.flatMap(uc => uc.pages))
    ]);

    setAllowedWidgetsByPage(
      activeUseCases.reduce((acc, uc) => {
        Object.entries(uc.widgets).forEach(([page, widgets]) => {
          acc[page] = acc[page] || new Set();
          widgets.forEach(w => acc[page].add(w));
        });
        return acc;
      }, {})
    );

  }, [projectDetails]);

  useEffect(() => {
  if (!projectStatus) return;

  const { dashboardStatus } = projectStatus;

  switch (dashboardStatus) {
    case "BLOCK_PREVIOUS_STEP":
      setDashboardGate({ type: "BLOCK_PREVIOUS_STEP" });
      break;

    case "FIRST_TIME_BUILD":
      setDashboardGate(null);
      setSetupStep(0);
      break;

    case "ALLOW_REBUILD":
      setDashboardGate({ type: "ALLOW_REBUILD" });
      break;

    case "BLOCK_REBUILD_LIMIT":
      setDashboardGate({ type: "BLOCK_REBUILD_LIMIT" });
      break;

    default:
      setDashboardGate(null);
  }
}, [projectStatus]);

  const moveToHome = () => {
    navigate("/selectmodel");
  };



  const saveDashboardSettings = async () => {
    setLoading(true)
    try{
      await axios.post('/save-dashboard-settings', {
        username,
        organizationId,
        projectName: projectName,
        pages: pageConfig,
      });
      message.success('Dashboard settings saved successfully.');
      setLoading(false)
    }catch(err) {
      setLoading(false)
      if (err.response && err.response.data) {
        message.warning(err.response.data.msg ? err.response.data.msg : err.response.data);
      } else if (err.response && err.response.data && err.response.data.userName) {
        message.warning(err.response.data.userName[0]);
        } else if (err.response && err.response.status === 401) {
        message.error('User not authorised');
      } else {
        message.error('Something went wrong');
      }
    }
  };

  const addRow = () => {
  const rows = [
    ...pageConfig[selectedPage].rows,
    { cols: 2, widgets: Array(2).fill(null) }
  ];

  setPageConfig({
    ...pageConfig,
    [selectedPage]: { ...pageConfig[selectedPage], rows }
  });
};

const removeRow = (rowIndex) => {
  const rows = pageConfig[selectedPage].rows.filter((_, i) => i !== rowIndex);

  setPageConfig({
    ...pageConfig,
    [selectedPage]: { ...pageConfig[selectedPage], rows }
  });
};

const updateCols = (rowIndex, cols) => {
  const rows = [...pageConfig[selectedPage].rows];

  rows[rowIndex] = {
    cols,
    widgets: Array(cols)
      .fill(null)
      .map((_, i) => rows[rowIndex].widgets[i] || null)
  };

  setPageConfig({
    ...pageConfig,
    [selectedPage]: { ...pageConfig[selectedPage], rows }
  });
};

  const handleSave = () => {
  Modal.confirm({
    title: 'Save & Lock Dashboard?',
    icon: <WarningOutlined />,
    content: 'Once locked, you cannot edit without unlocking.',
    okText: 'Save & Lock',
    onOk: async () => {
      const success = await saveDashboardSettings(true);
      if (success) {
        setIsLocked(true);
        setSetupStep(2);
      }
    }
  });
};

  
  const isSetupComplete = () => {
  const enabledPages = Object.entries(pageConfig)
    .filter(([_, config]) => config.enabled);

  for (const [pageKey, config] of enabledPages) {
    if (!['dashboard', 'report'].includes(pageKey)) continue;

    const hasWidget = config.rows.some(row =>
      row.widgets.some(w => w !== null)
    );

    if (!hasWidget) return false;
  }
  return true;
};

  const handleDragStart = (_, widget) => {
  if (isLocked) return;
  setDraggedWidget(widget);
};

  const handleDragOver = (e) => {
  if (isLocked) return;
  e.preventDefault();
};

const handleDrop = (e, rowIndex, colIndex) => {
  if (isLocked || !draggedWidget) return;
  e.preventDefault();

  const rows = pageConfig[selectedPage].rows.map(r => ({
    ...r,
    widgets: [...r.widgets]
  }));

  // Remove widget if already placed
  rows.forEach(r =>
    r.widgets.forEach((w, i) => {
      if (w?.id === draggedWidget.id) r.widgets[i] = null;
    })
  );

  if (rows[rowIndex].widgets[colIndex]) {
    message.warning('Cell already occupied');
    return;
  }

  rows[rowIndex].widgets[colIndex] = draggedWidget;

  setPageConfig({
    ...pageConfig,
    [selectedPage]: { ...pageConfig[selectedPage], rows }
  });

  setDraggedWidget(null);
};


const handleRemoveWidget = (id) => {
  const rows = pageConfig[selectedPage].rows.map(row => ({
    ...row,
    widgets: row.widgets.map(w => (w?.id === id ? null : w))
  }));

  setPageConfig({
    ...pageConfig,
    [selectedPage]: { ...pageConfig[selectedPage], rows }
  });
};

  const getWidgetsForPage = (page) => {
    return Array.from(allowedWidgetsByPage[page] || [])
      .map(widgetId => ({
        id: widgetId,
        ...widgetRegistry[widgetId]
      }))
      .filter(w => w.title); // safety
  };

  if (dashboardGate) {
  return (
    <DashboardGateCard
      type={dashboardGate.type}
      onAction={(action) => {
        if (action === "MODEL_CONFIG") {
          setDashboardGate(null);
          navigate('/selectmodel')
        }
        if (action === "REBUILD") {
          setDashboardGate(null);
          setSetupStep(0);
        }
      }}
    />
  );
}

  if (setupStep === 0) {
  const enabledCount = allowedPages.filter(p => pageConfig[p]?.enabled).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-6 pt-4">
        <Breadcrumb
          className="text-base text-[#1A59A7] font-Ubuntu tracking-wider"
          items={[
            { title: <a onClick={moveToHome}>Select Modal</a> },
            { title: "Dashboard Builder" },
          ]}
        />
      </div>
      <div className="flex justify-center items-center min-h-[calc(100vh-80px)]">
        <Card className="w-full max-w-3xl shadow-xl rounded-2xl border-0">

          {/* HEADER */}
          <div className="text-center mb-6">
            <SettingOutlined className="text-5xl text-blue-600 mb-4" />
            <h2 className="text-3xl font-bold mb-2">
              Configure Dashboard Modules
            </h2>
            <p className="text-gray-500">
              Available modules are based on selected category & use cases
            </p>
          </div>

          {/* ✅ ONLY ALLOWED MODULES */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            {allowedPages.map(key => {
              const meta = getPageMeta(key);
              const enabled = pageConfig[key]?.enabled;

              return (
                <div
                  key={key}
                  onClick={() =>
                    setPageConfig(prev => ({
                      ...prev,
                      [key]: { ...prev[key], enabled: !enabled }
                    }))
                  }
                  className={`cursor-pointer rounded-xl p-6 transition-all duration-200 ${
                    enabled
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg'
                      : 'bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`text-3xl ${enabled ? 'text-white' : 'text-blue-600'}`}>
                        {meta.icon}
                      </div>
                      <h3 className="font-bold text-lg">{meta.title}</h3>
                    </div>

                    {enabled && (
                      <div className="bg-white text-blue-600 rounded-full w-7 h-7 flex items-center justify-center font-bold">
                        ✓
                      </div>
                    )}
                  </div>

                  <p className={`text-sm ${enabled ? 'text-blue-100' : 'text-gray-500'}`}>
                    {meta.description}
                  </p>
                </div>
              );
            })}

          </div>

          <Button
            type="primary"
            size="large"
            loading={loading}
            disabled={enabledCount === 0}
            onClick={() => setSetupStep(1)}
            className="w-full h-12 font-semibold"
          >
            Continue to Builder
          </Button>
        </Card>
      </div>
    </div>
  );
}

  const pageMeta = getPageMeta(selectedPage);

  return (
  <div className="flex h-screen bg-gray-50">
    {/* SIDEBAR */}
    <div className="w-20 lg:w-64 bg-white border-r border-gray-200 flex flex-col shadow-sm">
      {/* BACK */}
      <div className="p-4 border-b">
        <Button
          type="text"
          onClick={() => setSetupStep(0)}
          className="flex items-center gap-2"
        >
          ← Back to Module Selection
        </Button>
      </div>

      {/* PAGES */}
      <nav className="flex-1 p-2 lg:p-4 space-y-2 overflow-y-auto">
        {Object.keys(pageConfig)
        .filter(k => pageConfig[k].enabled)
        .map(k => {
          const meta = getPageMeta(k);

          return (
            <div
              key={k}
              onClick={() => setSelectedPage(k)}
              className={`flex items-center gap-3 p-3 lg:p-4 rounded-lg cursor-pointer transition-all ${
                selectedPage === k
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl">{meta.icon}</span>
              <span className="hidden lg:block font-medium">
                {meta.title}
              </span>
            </div>
          );
        })}

      </nav>

      {/* SAVE */}
      {!isLocked && (
        <div className="p-4 border-t">
          <Button
            type="primary"
            danger
            block
            icon={<LockOutlined />}
            onClick={handleSave}
            disabled={!isSetupComplete()}
          >
            Save & Lock
          </Button>
        </div>
      )}
    </div>

    {/* MAIN */}
    <div className="flex-1 flex flex-col">
      {/* HEADER */}
        <div className="bg-white border-b px-6 py-4 shadow-sm">
          <div className="flex items-center gap-3">
            <span className="text-3xl text-blue-600">
              {pageMeta.icon}
            </span>
            <div>
              <h2 className="text-2xl font-bold">
                {pageMeta.title}
              </h2>
              <p className="text-sm text-gray-500">
                {isLocked ? 'View only mode' : 'Drag & drop widgets'}
              </p>
            </div>
          </div>
        </div>


      {/* CONTENT */}
      <div className="flex-1 overflow-auto p-6">
        {isBuilderPage ? (
          <div className="space-y-8 max-w-7xl mx-auto">
            {pageConfig[selectedPage].rows.map((row, rIdx) => (
              <div key={rIdx} className="space-y-3">
                {/* GRID */}
                <div
                  className="grid gap-4"
                  style={{ gridTemplateColumns: `repeat(${row.cols}, 1fr)` }}
                >
                  {row.widgets.map((cell, cIdx) => (
                    <div
                      key={cIdx}
                      draggable={!!cell && !isLocked}
                      onDragStart={(e) => cell && handleDragStart(e, cell)}
                      className={cell && !isLocked ? 'cursor-move' : ''}
                    >
                      <GridCell
                        widget={cell}
                        rowIndex={rIdx}
                        colIndex={cIdx}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onRemove={handleRemoveWidget}
                        isLocked={isLocked}
                      />
                    </div>
                  ))}
                </div>

                {/* ROW CONTROLS (EXACT LIKE OLD CODE) */}
                {!isLocked && (
                  <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <span className="text-sm font-medium">Columns:</span>
                    <InputNumber
                      min={1}
                      max={6}
                      value={row.cols}
                      onChange={(v) => updateCols(rIdx, v)}
                    />
                    <Button
                      danger
                      icon={<MinusOutlined />}
                      onClick={() => removeRow(rIdx)}
                    >
                      Remove Row
                    </Button>
                  </div>
                )}
              </div>
            ))}

            {/* ADD ROW — ONLY ONCE */}
            {!isLocked && (
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addRow}
                block
                size="large"
              >
                Add New Row
              </Button>
            )}
          </div>
        ) : (
          <>
            {selectedPage === 'camera' && <CameraDefaultView />}
            {selectedPage === 'alert' && <AlertDefaultView />}
          </>
        )}
      </div>
    </div>

    {/* WIDGET PANEL */}
  {/* WIDGET PANEL */}
{!isLocked && isBuilderPage && (
  <div className="w-72 lg:w-80 border-l border-gray-200 bg-white flex flex-col shadow-sm">
    
    {/* HEADER */}
    <div className="p-4 lg:p-6 border-b border-gray-200">
      <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
        <DragOutlined className="text-blue-600" />
        Available Widgets
      </h3>
      <p className="text-sm text-gray-500 mt-1">
        Drag to add to dashboard
      </p>
    </div>

    {/* LIST */}
    <div className="flex-1 overflow-y-auto p-4 space-y-3">
      {getWidgetsForPage(selectedPage).map(widget => {
        const used = pageConfig[selectedPage].rows.some(row =>
          row.widgets.some(w => w?.id === widget.id)
        );

        return (
          <div
            key={widget.id}
            draggable={!used}
            onDragStart={(e) => !used && handleDragStart(e, widget)}
            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
              used
                ? 'opacity-40 bg-gray-50 border-gray-200 cursor-not-allowed'
                : 'cursor-move bg-white border-blue-200 hover:border-blue-400 hover:shadow-md hover:scale-105'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DragOutlined className={used ? 'text-gray-400' : 'text-blue-600'} />
                <span className={`font-medium ${
                  used ? 'text-gray-400' : 'text-gray-800'
                }`}>
                  {widget.title}
                </span>
              </div>

               <span
                className={`text-[10px] px-2 py-[2px] rounded-full font-semibold
                  ${
                    widget.type === "graph"
                      ? "bg-blue-50 text-blue-600 border border-blue-200"
                      : "bg-green-50 text-green-600 border border-green-200"
                  }
                `}
              >
                {widget?.type?.toUpperCase()}
              </span>
            </div>

            {used && (
              <p className="text-xs text-gray-400 mt-2">
                Already in use
              </p>
            )}
          </div>
        );
      })}
    </div>
  </div>
)}

  </div>
);

};



export default DashboardBuilder;